import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Segmented,
  DatePicker,
  Button,
  Table,
  Typography,
  Spin,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import { useMemberMetricsService } from "../../../services/members/MemberMetricsServiceProvider";
import {
  MembershipMetrics,
  MembershipMetricsBucket,
  MembershipMetricGranularity,
  MembershipBreakdownItem,
  DEFAULT_RENEWAL_GRACE_DAYS,
} from "../../../model/members/MembershipMetrics";
import { Alert } from "../../../ui/primitives/Alert";
import { Select } from "../../../ui/primitives/Select";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import {
  DEFAULT_CANCELLATION_REASONS,
  DEFAULT_SIGNUP_HOW_THEY_HEARD,
} from "../../../model/client/ClientSettings";
import { relabelBreakdown } from "./relabelBreakdown";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const JOINS_COLOR = "#1D9E75";
const CANCELLATIONS_COLOR = "#dc2626";
const TOTAL_COLOR = "#185FA5";
const NET_POSITIVE_COLOR = "#1D9E75";
const NET_NEGATIVE_COLOR = "#dc2626";

// Palette reused across the breakdown panels.
const BREAKDOWN_COLORS = ["#185FA5", "#1D9E75", "#D85A30", "#d97706", "#9ca3af", "#0a1c3a"];

// Late-rollover window options, in days. A membership renewal is recorded when the club processes it,
// not when it takes effect: a direct-debit run completed on the 2nd of the month rolls every monthly
// member's expiry a day or two after it technically lapsed. Without a window, each of those members
// reads as a leaver on the last of the month and a joiner two days later — a churn spike that never
// happened. The dropdown stops at 14 days because past a fortnight the window starts hiding genuine
// lapses; the API accepts up to 31 for anyone calling it directly.
const LATE_ROLLOVER_OPTIONS = [
  { label: "Off — count every gap", value: 0 },
  { label: "1 day", value: 1 },
  { label: "2 days", value: 2 },
  { label: `${DEFAULT_RENEWAL_GRACE_DAYS} days (default)`, value: DEFAULT_RENEWAL_GRACE_DAYS },
  { label: "7 days", value: 7 },
  { label: "14 days", value: 14 },
];

// The reason the control exists, stated on the screen rather than hidden in a tooltip: a reader
// comparing two exports needs to know which setting produced the numbers in front of them.
const LATE_ROLLOVER_EXPLANATION =
  "Renewals are recorded when the club processes them, not when they take effect — a direct debit " +
  "run completed on the 2nd of the month rolls memberships forward a day or two after they expired " +
  "on paper. Set the window to Off to see the raw, unadjusted figures.";

function lateRolloverHeading(days: number | null): string {
  if (days == null) return "Late rollover window: not reported";
  return days > 0
    ? `Late rollover window: ${days} ${days === 1 ? "day" : "days"}`
    : "Late rollover window: off";
}

function describeLateRollover(days: number | null): string {
  if (days == null) {
    return "This server did not report which window it applied, so these figures cannot be attributed " +
      "to one. Refresh once the API is up to date.";
  }
  if (days <= 0) return "Late rollovers not allowed for — every gap counts as a member lost";
  return `Renewals recorded up to ${days} ${days === 1 ? "day" : "days"} after a membership expired ` +
    "count as continuous, not as a member lost and re-joining";
}

// Short form for the chart subtitle and the table header.
function lateRolloverTag(days: number | null): string {
  if (days == null) return "late rollover window not reported";
  return days > 0 ? `${days}-day late rollover window` : "late rollover window off";
}

function netColor(value: number): string | undefined {
  if (value > 0) return NET_POSITIVE_COLOR;
  if (value < 0) return NET_NEGATIVE_COLOR;
  return undefined;
}

function formatPeriodShort(
  periodStart: string,
  granularity: MembershipMetricGranularity
): string {
  const start = dayjs(periodStart);
  if (!start.isValid()) return periodStart;
  return granularity === "week" ? start.format("DD MMM") : start.format("MMM YY");
}

function formatPeriodLong(
  periodStart: string,
  granularity: MembershipMetricGranularity
): string {
  const start = dayjs(periodStart);
  if (!start.isValid()) return periodStart;
  return granularity === "week"
    ? `Week of ${start.format("DD MMM YYYY")}`
    : start.format("MMM YYYY");
}

interface BreakdownPanelProps {
  title: string;
  subtitle: string;
  items: MembershipBreakdownItem[];
  showPercent: boolean;
  testId: string;
}

const BreakdownPanel: React.FC<BreakdownPanelProps> = ({
  title,
  subtitle,
  items,
  showPercent,
  testId,
}) => {
  const total = items.reduce((sum, i) => sum + i.count, 0);
  return (
    <Card size="small" style={{ height: "100%" }} data-testid={testId}>
      <div style={{ fontWeight: 600, color: "#0a1c3a" }}>{title}</div>
      <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 12 }}>
        {subtitle}
      </Text>
      {items.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No data for this period
        </Text>
      ) : (
        items.map((item, index) => (
          <div
            key={item.name}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "4px 0",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length],
                  display: "inline-block",
                }}
              />
              {item.name}
            </span>
            <span style={{ fontWeight: 500 }}>
              {showPercent && total > 0
                ? `${Math.round((item.count / total) * 100)}%`
                : item.count}
            </span>
          </div>
        ))
      )}
    </Card>
  );
};

const RetentionReporting: React.FC = () => {
  const metricsService = useMemberMetricsService();
  const { signup, cancellation } = useClientConfig();

  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().subtract(8, "month").startOf("month"),
    dayjs(),
  ]);
  const [granularity, setGranularity] =
    useState<MembershipMetricGranularity>("month");
  const [lateRolloverDays, setLateRolloverDays] = useState<number>(
    DEFAULT_RENEWAL_GRACE_DAYS
  );
  const [metrics, setMetrics] = useState<MembershipMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchMetrics = useCallback(async () => {
    const [from, to] = range;
    if (!from || !to) return;
    setLoading(true);
    try {
      const result = await metricsService.getMembershipMetrics(
        from.format("YYYY-MM-DD"),
        to.format("YYYY-MM-DD"),
        granularity,
        lateRolloverDays
      );
      setMetrics(result);
    } catch {
      // The base API client already surfaces the failure via a notification.
    } finally {
      setLoading(false);
    }
  }, [metricsService, range, granularity, lateRolloverDays]);

  // What the figures on screen were actually computed with: the API clamps out-of-range values, so
  // label the applied window rather than the requested one. Before the first response lands there are
  // no figures to describe, so the requested value is the honest label.
  const appliedLateRolloverDays = metrics === null ? lateRolloverDays : metrics.renewalGraceDays;

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const chartData = useMemo(
    () =>
      (metrics?.buckets ?? []).map((b) => ({
        period: formatPeriodShort(b.periodStart, granularity),
        joins: b.newMembers,
        cancellations: b.lostMembers,
        total: b.closingTotal,
      })),
    [metrics, granularity]
  );

  // The API returns option codes; the client's configured labels are what staff recognise. A tenant
  // that has never saved signup settings serves no list, so fall back to the defaults like the signup
  // forms do — an absent list must not take the screen down.
  const howTheyHeardItems = useMemo(
    () =>
      relabelBreakdown(
        metrics?.howTheyHeard ?? [],
        signup?.howTheyHeard ?? DEFAULT_SIGNUP_HOW_THEY_HEARD
      ),
    [metrics, signup?.howTheyHeard]
  );

  // Same treatment for cancellation reasons. Historic rows hold free text rather than a configured
  // code, so the pass-through in relabelBreakdown is doing real work here — those rows show verbatim.
  const cancellationReasonItems = useMemo(
    () =>
      relabelBreakdown(
        metrics?.cancellationReasons ?? [],
        cancellation?.reasons ?? DEFAULT_CANCELLATION_REASONS
      ),
    [metrics, cancellation?.reasons]
  );

  const tableColumns: ColumnsType<MembershipMetricsBucket> = useMemo(
    () => [
      {
        title: granularity === "week" ? "Week" : "Month",
        dataIndex: "periodStart",
        key: "periodStart",
        render: (value: string) => formatPeriodLong(value, granularity),
      },
      { title: "Opening total", dataIndex: "openingTotal", key: "openingTotal", align: "right" },
      {
        title: "Joins",
        dataIndex: "newMembers",
        key: "newMembers",
        align: "right",
        render: (v: number) => <Text style={{ color: JOINS_COLOR }}>+{v}</Text>,
      },
      {
        title: "Members lost",
        dataIndex: "lostMembers",
        key: "lostMembers",
        align: "right",
        render: (v: number) => <Text style={{ color: CANCELLATIONS_COLOR }}>-{v}</Text>,
      },
      {
        title: "Net movement",
        dataIndex: "netGrowth",
        key: "netGrowth",
        align: "right",
        render: (v: number) => (
          <Text strong style={{ color: netColor(v) }}>
            {v > 0 ? `+${v}` : v}
          </Text>
        ),
      },
      {
        title: "Closing total",
        dataIndex: "closingTotal",
        key: "closingTotal",
        align: "right",
        render: (v: number) => <Text strong>{v}</Text>,
      },
    ],
    [granularity]
  );

  return (
    <div
      data-testid="retention-reporting-screen"
      style={{ padding: 24, boxSizing: "border-box" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0, color: "#0a1c3a", letterSpacing: "0.03em" }}>
            RETENTION REPORTING
          </Title>
          <Text type="secondary">
            Membership trends, joins, cancellations and retention data
          </Text>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(values) => {
              if (values && values[0] && values[1]) {
                setRange([values[0], values[1]]);
              }
            }}
          />
          <Segmented
            data-testid="retention-reporting-granularity"
            value={granularity}
            onChange={(value) => setGranularity(value as MembershipMetricGranularity)}
            options={[
              { label: "Monthly", value: "month" },
              { label: "Weekly", value: "week" },
            ]}
          />
          <Select
            label="Late rollover window"
            data-testid="retention-reporting-late-rollover"
            value={lateRolloverDays}
            onChange={(value) => setLateRolloverDays(Number(value))}
            options={LATE_ROLLOVER_OPTIONS}
            // The primitive stacks its label above the field, making it taller than the unlabelled
            // controls beside it — bottom-align so every control in the row sits on one baseline.
            style={{ minWidth: 200, alignSelf: "flex-end" }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchMetrics} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <Alert
        data-testid="retention-reporting-late-rollover-banner"
        type="info"
        style={{ marginBottom: 16 }}
        message={lateRolloverHeading(appliedLateRolloverDays)}
        description={
          <>
            <div>{describeLateRollover(appliedLateRolloverDays)}</div>
            <div style={{ marginTop: 4 }}>{LATE_ROLLOVER_EXPLANATION}</div>
          </>
        }
      />

      <Spin spinning={loading}>
        {/* KPI cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={5}>
            <Card data-testid="retention-kpi-total">
              <Statistic title="Total members" value={metrics?.totalActiveMembers ?? 0} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {/* metrics.to is already normalised to a plain YYYY-MM-DD string by the service
                    (DateReviver-proof), so dayjs parses it directly. */}
                As of {metrics ? dayjs(metrics.to).format("DD MMM YYYY") : "—"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Card data-testid="retention-kpi-joins">
              <Statistic
                title="Joins in period"
                value={metrics?.totalNewMembers ?? 0}
                valueStyle={{ color: JOINS_COLOR }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Card data-testid="retention-kpi-cancellations">
              <Statistic
                title="Members lost in period"
                value={metrics?.totalLostMembers ?? 0}
                valueStyle={{ color: CANCELLATIONS_COLOR }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card data-testid="retention-kpi-net">
              <Statistic
                title="Net growth"
                value={metrics?.netGrowth ?? 0}
                valueStyle={{ color: netColor(metrics?.netGrowth ?? 0) }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Card data-testid="retention-kpi-conversion">
              <Statistic
                title="Lead → Member conversion"
                value={metrics?.leadToMemberConversionRate ?? 0}
                precision={2}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card data-testid="retention-chart-joins-vs-cancellations">
              <div style={{ fontWeight: 600, color: "#0a1c3a" }}>Joins vs Members lost</div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {granularity === "week" ? "Weekly" : "Monthly"} comparison ·{" "}
                {lateRolloverTag(appliedLateRolloverDays)}
              </Text>
              <div style={{ height: 260, marginTop: 12 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                    <CartesianGrid stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="joins"
                      name="Joins"
                      stroke={JOINS_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cancellations"
                      name="Members lost"
                      stroke={CANCELLATIONS_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card data-testid="retention-chart-total-trend">
              <div style={{ fontWeight: 600, color: "#0a1c3a" }}>Total membership trend</div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Running total over time
              </Text>
              <div style={{ height: 260, marginTop: 12 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                    <CartesianGrid stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} domain={["auto", "auto"]} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Total members"
                      stroke={TOTAL_COLOR}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Breakdowns */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={8}>
            <BreakdownPanel
              title="How They Heard About Us"
              subtitle="What brought new members to the club"
              items={howTheyHeardItems}
              showPercent
              testId="retention-breakdown-how-they-heard"
            />
          </Col>
          <Col xs={24} lg={8}>
            <BreakdownPanel
              title="Reason for Leaving"
              subtitle="Why members leave"
              items={cancellationReasonItems}
              showPercent
              testId="retention-breakdown-cancellation-reasons"
            />
          </Col>
          <Col xs={24} lg={8}>
            <BreakdownPanel
              title="Membership type"
              subtitle="Current active members by type"
              items={metrics?.membershipTypes ?? []}
              showPercent={false}
              testId="retention-breakdown-membership-type"
            />
          </Col>
        </Row>

        {/* Month-by-month table */}
        <Card
          data-testid="retention-reporting-table"
          title={granularity === "week" ? "Week-by-week net movement" : "Month-by-month net movement"}
          size="small"
          extra={
            <Text type="secondary" style={{ fontSize: 11 }}>
              {`${lateRolloverTag(appliedLateRolloverDays)} applied`}
            </Text>
          }
        >
          <Table<MembershipMetricsBucket>
            rowKey="periodStart"
            size="small"
            columns={tableColumns}
            dataSource={metrics?.buckets ?? []}
            pagination={false}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default RetentionReporting;
